import React, { useEffect, useRef, useState } from "react";
import { ChatResponse, sendChatMessage } from "../api/client";

type ChatMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
};

interface ChatProps {
  slug: string;
  botName: string;
  /**
   * Optional language code, e.g. "en", "it".
   * If not provided, Chat will try to read ?lang= from the URL, falling back to "en".
   */
  lang?: string;
}

// Simple localized strings
const STRINGS = {
  en: {
    empty: (botName: string) =>
      `You’re chatting with ${botName}. Ask anything about their website.`,
    placeholder: (botName: string) => `Ask ${botName} a question...`,
    sending: "Sending...",
    send: "Send",
    you: "You",
    error: "Sorry, there was an error sending your message. Please try again.",
  },
  it: {
    empty: (botName: string) =>
      `Stai parlando con ${botName}. Chiedimi qualsiasi cosa sul loro sito.`,
    placeholder: (botName: string) => `Fai una domanda a ${botName}...`,
    sending: "Invio...",
    send: "Invia",
    you: "Tu",
    error:
      "Si è verificato un errore nell'invio del messaggio. Per favore riprova.",
  },
} as const;

type LocaleKey = keyof typeof STRINGS;

const Chat: React.FC<ChatProps> = ({ slug, botName, lang }) => {
  // Resolve language:
  // 1) explicit prop (preferred)
  // 2) ?lang= in URL (if in browser)
  // 3) default "en"
  let resolvedLang = (lang || "en").toLowerCase();
  if (!lang && typeof window !== "undefined") {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlLang = params.get("lang");
      if (urlLang) {
        resolvedLang = urlLang.toLowerCase();
      }
    } catch {
      // ignore URL parsing errors and keep default
    }
  }

  const locale =
    STRINGS[resolvedLang as LocaleKey] ?? STRINGS.en;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages.length]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    setError(null);

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: trimmed,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsSending(true);

    try {
      const response: ChatResponse = await sendChatMessage(slug, {
        message: trimmed,
        conversationId,
      });

      if (
        response.conversationId &&
        response.conversationId !== conversationId
      ) {
        setConversationId(response.conversationId);
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: response.reply,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to send message");
      const systemMessage: ChatMessage = {
        id: `system-${Date.now()}`,
        role: "system",
        content: locale.error,
      };
      setMessages((prev) => [...prev, systemMessage]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    e
  ) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-empty">
            <p>
              {locale.empty(botName)}
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`chat-message chat-message-${msg.role}`}
          >
            <div className="chat-message-bubble">
              {msg.role !== "system" && (
                <div className="chat-message-role">
                  {msg.role === "user" ? locale.you : botName}
                </div>
              )}
              <div className="chat-message-content">{msg.content}</div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        {error && <div className="chat-error">{error}</div>}
        <textarea
          className="chat-input"
          placeholder={locale.placeholder(botName)}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          maxLength={2000}
        />
        <button
          className="chat-send-button"
          onClick={handleSend}
          disabled={isSending || !input.trim()}
        >
          {isSending ? locale.sending : locale.send}
        </button>
      </div>
    </div>
  );
};

export default Chat;
